import { useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";
import { api, errorText } from "../lib/api";
import { Input } from "../components/ui/input";

export const PAY_STYLE = { created: "bg-champagne text-charcoal", partially_paid: "bg-champagne text-charcoal", paid: "bg-wine text-ivory", cancelled: "bg-line text-charcoal", expired: "bg-line text-charcoal" };
export const PAY_LABEL = { created: "Pending", partially_paid: "Part paid", paid: "Paid", cancelled: "Cancelled", expired: "Expired" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const PayBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`px-2 py-1 text-[0.65rem] uppercase tracking-[0.1em] ${PAY_STYLE[status] || "bg-line"}`}>{PAY_LABEL[status] || status}</span>
);

export const PaymentPanel = ({ enquiry, onChange }) => {
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState(`Advance for ${enquiry.brand_name} — ${enquiry.service}`);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const links = enquiry.payments || [];

  const create = async () => {
    if (!(Number(amount) > 0)) return toast.error("Please enter an amount.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${enquiry.id}/payment-links`, { amount: Number(amount), description: desc, notify_email: notify });
      onChange({ ...enquiry, payments: [...links, data] });
      setAmount("");
      await navigator.clipboard?.writeText(data.short_url).catch(() => {});
      toast.success(notify ? "Link created and emailed to the client" : "Link created and copied");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (l) => {
    try {
      await api.post(`/admin/enquiries/${enquiry.id}/payment-links/${l.id}/cancel`);
      onChange({ ...enquiry, payments: links.map((x) => (x.id === l.id ? { ...x, status: "cancelled" } : x)) });
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div className="mt-2 border border-line bg-white p-4" data-testid="payment-panel">
      <p className="text-xs font-medium uppercase tracking-[0.12em] text-wine">Payment link (Razorpay)</p>
      {links.map((l) => (
        <div key={l.id} className="mt-3 flex flex-wrap items-center gap-2 border-b border-line pb-3 text-sm" data-testid={`payment-link-${l.id}`}>
          <span className="font-medium">{inr(l.amount)}</span>
          <PayBadge status={l.status} />
          <span className="min-w-0 flex-1 truncate text-xs text-muted-ink">{l.description}</span>
          <button onClick={() => { navigator.clipboard?.writeText(l.short_url); toast.success("Link copied"); }} className="p-1 text-wine" aria-label="Copy link"><Copy size={14} /></button>
          <a href={l.short_url} target="_blank" rel="noopener noreferrer" className="p-1 text-wine" aria-label="Open link"><ExternalLink size={14} /></a>
          {l.status === "created" && <button onClick={() => cancel(l)} className="text-xs text-muted-ink underline" data-testid={`payment-cancel-${l.id}`}>Cancel</button>}
        </div>
      ))}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-ink">₹</span>
          <Input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="pl-7" data-testid="payment-amount" />
        </div>
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} className="sm:col-span-2" data-testid="payment-description" />
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-charcoal/80">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="payment-notify" /> Razorpay emails the link to {enquiry.email}
      </label>
      <button onClick={create} disabled={busy} className="btn btn-primary mt-4 !px-4 !py-2.5 disabled:opacity-60" data-testid="payment-create-btn">{busy ? "Creating…" : "Create payment link"}</button>
    </div>
  );
};
